import React, { useMemo } from 'react';
import { useCms } from '../context/CmsContext';
import { CLIENTS_LIST } from '../data/clientsData';

interface ClientLogoItem {
  id: string;
  src: string;
  alt: string;
}

export const ClientLogosMarquee: React.FC = () => {
  const { siteData } = useCms();

  const clientsList = useMemo(() => {
    const customList = siteData?.pages?.clients?.clientsList;
    if (Array.isArray(customList) && customList.length > 0) {
      return customList;
    }
    return CLIENTS_LIST;
  }, [siteData?.pages?.clients?.clientsList]);

  // Seamless loop with dual concatenated dataset from dynamic client items
  const marqueeItems = useMemo(() => {
    const list: ClientLogoItem[] = clientsList.map((client: any, idx: number) => ({
      id: String(client.id || idx + 1),
      src: client.logoSrc || client.src || `/images/clients/${idx + 1}.png`,
      alt: client.name || client.alt || `Client Partner ${idx + 1}`,
    }));
    return [...list, ...list];
  }, [clientsList]);

  return (
    <section className="soft-section-reveal client-marquee-section w-full bg-white py-8 sm:py-10 border-t border-[#1b6f7e]/15 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header Label */}
        <div className="flex items-center justify-start gap-2.5 mb-3 sm:mb-3.5 px-2">
          <span className="w-2 h-2 rounded-none bg-[#1b7481] animate-pulse" />
          <span className="text-xs sm:text-[13px] font-mono uppercase tracking-[0.2em] text-[#06242a]/80 font-medium">
            {siteData?.pages?.home?.partnersTitle || 'Trusted by Global OEM Giants'}
          </span>
        </div>

        {/* Marquee Track Container with Ambient Side Fades */}
        <div className="relative w-full overflow-hidden marquee-container py-1.5 select-none">
          
          {/* Left Gradient Edge Fade */}
          <div
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 sm:w-36 z-10"
            style={{ background: 'linear-gradient(to right, #ffffff, rgba(255, 255, 255, 0))' }}
          />

          {/* Right Gradient Edge Fade */}
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 sm:w-36 z-10"
            style={{ background: 'linear-gradient(to left, #ffffff, rgba(255, 255, 255, 0))' }}
          />

          {/* Continuous Smooth Infinite Logo Marquee Strip */}
          <div className="animate-marquee-infinite flex items-center">
            {marqueeItems.map((client, idx) => (
              <div
                key={`${client.id}-${idx}`}
                className="mx-0.5 sm:mx-1.5 md:mx-2 shrink-0 flex items-center justify-center group/logo cursor-pointer"
              >
                <div className="h-24 sm:h-30 md:h-36 w-36 sm:w-48 md:w-56 flex items-center justify-center p-1 opacity-95 hover:opacity-100 transition-all duration-300 hover:scale-105 group-hover/logo:drop-shadow-[0_10px_20px_rgba(6,36,42,0.16)]">
                  <img
                    src={client.src}
                    alt={client.alt}
                    width={180}
                    height={90}
                    decoding="async"
                    className="max-h-full max-w-full object-contain drop-shadow-2xs transition-all duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>

        </div>

      </div>
    </section>
  );
};
